<template>
  <Modal v-model="isOpen" content-class="max-w-lg" :show-close="true">
    <template #title>{{ t('title') }}</template>

    <div class="space-y-3">
      <div
        v-for="option in penaltyTypeOptions"
        :key="option.value"
        class="rounded-lg border p-4"
        :class="
          selectedType === option.value
            ? 'outline outline-2 outline-offset-2 outline-hazel-500'
            : ''
        "
      >
        <label class="flex cursor-pointer items-start gap-3">
          <input
            v-model="selectedType"
            type="radio"
            :value="option.value"
            :class="radiobutton({ class: 'mt-1 h-4 w-4' })"
          />
          <span class="text-sm">{{ option.label }}</span>
        </label>
      </div>

      <div v-if="showDepositChargeCheckbox" class="rounded-lg border p-4">
        <Checkbox v-model="attemptDepositCharge" :label="t('attemptDepositCharge')" />
      </div>
    </div>

    <div class="mt-6 flex justify-end gap-2">
      <CVButton variant="outline" :disabled="isSubmitting" @click="isOpen = false">
        {{ t('cancel') }}
      </CVButton>
      <CVButton
        variant="default"
        :disabled="!selectedType || isSubmitting"
        :is-loading="isSubmitting"
        data-testid="confirm-contract-penalty"
        @click="handleConfirm"
      >
        {{ t('confirm') }}
      </CVButton>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Checkbox from '@/components/Checkbox.vue';
import { radiobutton } from '@carvia/theme/lib/cva/radiobutton';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useStoredPaymentMethods } from '@/queries/use-payment-api';
import { ContractPenaltyType, CreditCard, VehicleExtraType } from '@carvia/ros-client-types';
import { getBookedExtras } from '@/utils/pricing.utils';

const props = defineProps<{
  booking: Booking;
  isSubmitting?: boolean;
}>();

const isOpen = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  (e: 'confirm', payload: { type: ContractPenaltyType; attemptDepositCharge?: boolean }): void;
}>();

const { t } = useI18n();

const selectedType = ref<ContractPenaltyType | null>(null);
const attemptDepositCharge = ref(true);

const isCrossborderBooked = computed(() =>
  getBookedExtras(props.booking.priceCalculation).some(
    (bookedExtra) =>
      bookedExtra.vehicleExtraSnapshot?.type === VehicleExtraType.CROSSBORDER &&
      bookedExtra.vehicleExtraOptionSnapshot?.value === 'true',
  ),
);

const { data: storedPaymentMethods } = useStoredPaymentMethods(props.booking.id);

const hasDepositPaymentMethod = computed(() =>
  storedPaymentMethods.value?.some((method) => method.purposes.includes(CreditCard.DEPOSIT)),
);

const showDepositChargeCheckbox = computed(
  () => hasDepositPaymentMethod.value && selectedType.value !== ContractPenaltyType.RACETRACK,
);

const penaltyTypeOptions = computed(() => {
  const options = [
    {
      value: ContractPenaltyType.ABROAD_OUTSIDE_AREA,
      label: t('types.ABROAD_OUTSIDE_AREA'),
    },
    {
      value: ContractPenaltyType.UNREGISTERED_DRIVER,
      label: t('types.UNREGISTERED_DRIVER'),
    },
    {
      value: ContractPenaltyType.RACETRACK,
      label: t('types.RACETRACK'),
    },
  ];

  if (!isCrossborderBooked.value) {
    options.unshift({
      value: ContractPenaltyType.ABROAD_WITHOUT_EXTRA,
      label: t('types.ABROAD_WITHOUT_EXTRA'),
    });
  }

  return options;
});

const resetForm = () => {
  selectedType.value = penaltyTypeOptions.value[0]?.value ?? null;
  attemptDepositCharge.value = true;
};

watch(isOpen, (open) => {
  if (open) {
    resetForm();
  }
});

watch(penaltyTypeOptions, () => {
  if (
    isOpen.value &&
    selectedType.value &&
    !penaltyTypeOptions.value.some((option) => option.value === selectedType.value)
  ) {
    resetForm();
  }
});

const handleConfirm = () => {
  if (!selectedType.value) {
    return;
  }

  emit('confirm', {
    type: selectedType.value,
    attemptDepositCharge: showDepositChargeCheckbox.value ? attemptDepositCharge.value : undefined,
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Add contract penalty",
    "confirm": "Add",
    "cancel": "Cancel",
    "attemptDepositCharge": "Charge deposit card",
    "types": {
      "ABROAD_WITHOUT_EXTRA": "Cross-border driving without booked extra",
      "ABROAD_OUTSIDE_AREA": "Cross-border driving outside permitted area",
      "UNREGISTERED_DRIVER": "Unregistered additional driver",
      "RACETRACK": "Driving on non-public road / race track"
    }
  },
  "de": {
    "title": "Strafgebühr hinzufügen",
    "confirm": "Hinzufügen",
    "cancel": "Abbrechen",
    "attemptDepositCharge": "Kautionskarte belasten",
    "types": {
      "ABROAD_WITHOUT_EXTRA": "Fahrt ins Ausland ohne Buchung des Extras",
      "ABROAD_OUTSIDE_AREA": "Fahrt ins Ausland außerhalb des erlaubten Gebiets",
      "UNREGISTERED_DRIVER": "Nicht angemeldeter Zusatzfahrer",
      "RACETRACK": "Fahrt auf nicht öffentlicher Straße / Rennstrecke"
    }
  },
  "es": {
    "title": "Añadir penalización contractual",
    "confirm": "Añadir",
    "cancel": "Cancelar",
    "attemptDepositCharge": "Cargar tarjeta de depósito",
    "types": {
      "ABROAD_WITHOUT_EXTRA": "Conducción internacional sin extra reservado",
      "ABROAD_OUTSIDE_AREA": "Conducción internacional fuera del área permitida",
      "UNREGISTERED_DRIVER": "Conductor adicional no registrado",
      "RACETRACK": "Conducción en vía no pública / circuito"
    }
  }
}
</i18n>
